import { formatInt, formatKg } from "./number";
/**
 * Kilograms and pounds at the EDGE (`D266`) — PURE.
 *
 * ── STORAGE IS KILOGRAMS, ALWAYS ───────────────────────────────────────────
 * Every load the server stores, the engine progresses, a record compares and
 * the agent reasons over is kilograms. The unit a person chose is applied
 * here and only here: a number the person reads goes through
 * {@link toWeightUnit}, a number the person types comes back through
 * {@link fromWeightUnit}, and nothing in between ever holds pounds.
 *
 * ── THE ROUND TRIP IS EXACT WHERE IT MATTERS ───────────────────────────────
 * The pound is DEFINED as 0.45359237 kg, so a typed 135 lb is stored as
 * 61.23496995 kg — a full double, which is what `workout_set.load` (`REAL`)
 * keeps — and reads back as 135 lb to well within the 0.01 the dial rounds
 * to. No column stores the typed unit: the double carries the integer, and
 * `weight.test.ts` proves it over every half-pound from 0 to 1500.
 *
 * ── THE STEP IS A POUND STEP, NOT A CONVERTED ONE ──────────────────────────
 * A plan's `loadStepKg` of 2.5 converts to 5.51 lb, which no gym can load.
 * {@link loadStepInUnit} maps it to the nearest step a pound gym has —
 * 0.5, 1, 2.5, 5 or 10 lb — so the dial moves in plate-sized clicks in
 * either unit.
 */
/** The international pound, exactly, by definition (1959). */
export const KG_PER_LB = 0.453_592_37;
export function kgToLb(kg) {
    return kg / KG_PER_LB;
}
export function lbToKg(lb) {
    return lb * KG_PER_LB;
}
/**
 * Two decimals is the dial's precision — the same `stepBy` rounds to — so a
 * converted value is a number the dial can hold and compare equal again.
 */
function round2(value) {
    return Math.round(value * 100) / 100;
}
/** A stored kilogram value in the person's unit, rounded to 0.01. */
export function toWeightUnit(kg, unit) {
    return unit === "lb" ? round2(kgToLb(kg)) : kg;
}
/**
 * A value the person typed or dialled, back to kilograms for storage. NOT
 * rounded: the full double is what makes {@link toWeightUnit} return the
 * same pound figure (see the header).
 */
export function fromWeightUnit(value, unit) {
    return unit === "lb" ? lbToKg(value) : value;
}
/** The pound steps a gym can load (`D266`). */
export const LB_STEPS = [0.5, 1, 2.5, 5, 10];
/**
 * The dial's step in the person's unit for a plan step in kilograms. In
 * kilograms it is the plan's own step; in pounds it is the {@link LB_STEPS}
 * entry nearest the converted value (a tie goes to the smaller, the finer
 * control): 2.5 kg → 5 lb, 5 kg → 10 lb, 1.25 kg → 2.5 lb, 1 kg → 2.5 lb.
 */
export function loadStepInUnit(stepKg, unit) {
    if (unit === "kg") {
        return stepKg;
    }
    const target = kgToLb(stepKg);
    let best = LB_STEPS[0];
    for (const step of LB_STEPS) {
        if (Math.abs(step - target) < Math.abs(best - target)) {
            best = step;
        }
    }
    return best;
}
/** The highest load the dial offers, in the person's unit. */
export function maxLoadInUnit(maxKg, unit) {
    return unit === "lb" ? Math.round(kgToLb(maxKg)) : maxKg;
}
/**
 * A stored kilogram value as a bare number in the person's unit:
 * 61.23496995 → "135" (lb) / "61,2" (kg, pt-BR). No unit — the sentence
 * around it owns the words.
 */
export function formatWeightNumber(kg, unit, locale, options = {}) {
    return formatKg(toWeightUnit(kg, unit), locale, options);
}
/**
 * A stored kilogram value with its unit: "72,5 kg", "160 lb". The unit
 * symbol is the same in both languages — "lb" is what a Brazilian gym's
 * imported plates say too.
 */
export function formatWeight(kg, unit, locale, options = {}) {
    return `${formatWeightNumber(kg, unit, locale, options)} ${unit}`;
}
/**
 * A training volume (load × reps, summed), stored in kilograms: tonnes in
 * kilograms ("2,2 t"), whole pounds with grouping in pounds ("4,850 lb") —
 * nobody who reads pounds counts in short tons.
 */
export function formatVolume(kg, unit, locale) {
    if (unit === "lb") {
        return `${formatInt(Math.round(kgToLb(kg)), locale)} lb`;
    }
    return `${formatKg(kg / 1000, locale)} t`;
}
/**
 * The unit a person sees before they choose (`D266`): kilograms, in every
 * locale. An en-US account is OFFERED pounds (the settings row says so) but
 * never switched to them: a load that silently changed unit would be read as
 * a load that changed.
 */
export const DEFAULT_WEIGHT_UNIT = "kg";
/** A stored preference, or the default when the account never chose. */
export function weightUnitOrDefault(stored) {
    return stored ?? DEFAULT_WEIGHT_UNIT;
}
//# sourceMappingURL=weight.js.map